import React, { useEffect, useRef, useMemo } from 'react';
import { createChart, ColorType, BaselineSeries, AreaSeries } from 'lightweight-charts';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { ShowChart } from '@mui/icons-material';

const PALETTE = [
  { line: '#26a69a', fill1: 'rgba(38,166,154,0.28)', fill2: 'rgba(38,166,154,0.04)' },
  { line: '#7B61FF', fill1: 'rgba(123,97,255,0.28)', fill2: 'rgba(123,97,255,0.04)' },
  { line: '#FF9800', fill1: 'rgba(255,152,0,0.28)',   fill2: 'rgba(255,152,0,0.04)'  },
  { line: '#00BCD4', fill1: 'rgba(0,188,212,0.28)',   fill2: 'rgba(0,188,212,0.04)'  },
  { line: '#E91E63', fill1: 'rgba(233,30,99,0.28)',   fill2: 'rgba(233,30,99,0.04)'  },
  { line: '#2196F3', fill1: 'rgba(33,150,243,0.28)',  fill2: 'rgba(33,150,243,0.04)' },
];

const DD_LINE  = '#EF5350';
const DD_FILL1 = 'rgba(239,83,80,0.04)';
const DD_FILL2 = 'rgba(239,83,80,0.28)';
const BG       = '#131722';
const BG_HDR   = '#0f1724';
const GRID     = '#1e2a3b';
const TICK_CLR = '#9199a6';

function fmt(v, d = 2) {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
}

function buildChartData(trades) {
  let cum = 0, peak = 0, lastTime = 0;
  const cumData = [], ddData = [];
  let maxDd = 0, maxDdPct = 0;

  for (const t of trades) {
    cum += t.net_pnl || 0;
    if (cum > peak) peak = cum;
    const dd    = cum - peak;
    const ddPct = peak > 0 ? (dd / peak) * 100 : 0;
    if (dd < maxDd) { maxDd = dd; maxDdPct = ddPct; }

    let time = Math.floor(new Date(t.exit.created_at).getTime() / 1000);
    if (time <= lastTime) time = lastTime + 1;
    lastTime = time;

    cumData.push({ time, value: parseFloat(cum.toFixed(2)) });
    ddData.push({ time, value: parseFloat(dd.toFixed(2)) });
  }

  return { cumData, ddData, finalPnl: cum, maxDd, maxDdPct };
}

function SingleStrategyChart({ name, palette, trades }) {
  const cumRef = useRef(null);
  const ddRef  = useRef(null);

  const { cumData, ddData, finalPnl, maxDd, maxDdPct } = useMemo(() => {
    const closed = trades
      .filter(t => t.exit && t.net_pnl != null)
      .sort((a, b) => new Date(a.exit.created_at) - new Date(b.exit.created_at));
    if (!closed.length) return { cumData: [], ddData: [], finalPnl: 0, maxDd: 0, maxDdPct: 0 };
    return buildChartData(closed);
  }, [trades]);

  useEffect(() => {
    if (!cumRef.current || !ddRef.current || !cumData.length) return;

    const base = {
      layout: {
        background: { type: ColorType.Solid, color: BG },
        textColor: TICK_CLR,
        fontSize: 11,
      },
      grid: { vertLines: { color: GRID }, horzLines: { color: GRID } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: GRID, scaleMargins: { top: 0.08, bottom: 0.08 } },
      timeScale: { borderColor: GRID, timeVisible: true, secondsVisible: false },
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale:  { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
    };

    const cumChart = createChart(cumRef.current, { ...base, height: 230 });
    const cumSeries = cumChart.addSeries(BaselineSeries, {
      baseValue:       { type: 'price', price: 0 },
      topLineColor:    palette.line,
      topFillColor1:   palette.fill1,
      topFillColor2:   palette.fill2,
      bottomLineColor: DD_LINE,
      bottomFillColor1: DD_FILL1,
      bottomFillColor2: DD_FILL2,
      lineWidth: 2,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    });
    cumSeries.setData(cumData);
    cumChart.timeScale().fitContent();

    const ddChart = createChart(ddRef.current, { ...base, height: 120 });
    const ddSeries = ddChart.addSeries(AreaSeries, {
      lineColor:    DD_LINE,
      topColor:     DD_FILL1,
      bottomColor:  DD_FILL2,
      lineWidth: 1,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    });
    ddSeries.setData(ddData);
    ddChart.timeScale().fitContent();

    // Sync time-scale pan/zoom between the two panels
    let busy = false;
    cumChart.timeScale().subscribeVisibleLogicalRangeChange(r => {
      if (busy || !r) return;
      busy = true;
      ddChart.timeScale().setVisibleLogicalRange(r);
      busy = false;
    });
    ddChart.timeScale().subscribeVisibleLogicalRangeChange(r => {
      if (busy || !r) return;
      busy = true;
      cumChart.timeScale().setVisibleLogicalRange(r);
      busy = false;
    });

    // Sync crosshair move
    cumChart.subscribeCrosshairMove(p => {
      if (!p || !p.time) { ddChart.clearCrosshairPosition(); return; }
      ddChart.setCrosshairPosition(0, p.time, ddSeries);
    });
    ddChart.subscribeCrosshairMove(p => {
      if (!p || !p.time) { cumChart.clearCrosshairPosition(); return; }
      cumChart.setCrosshairPosition(0, p.time, cumSeries);
    });

    const ro = new ResizeObserver(() => {
      if (cumRef.current) cumChart.applyOptions({ width: cumRef.current.offsetWidth });
      if (ddRef.current)  ddChart.applyOptions({  width: ddRef.current.offsetWidth  });
    });
    if (cumRef.current) ro.observe(cumRef.current.parentElement || cumRef.current);

    return () => {
      ro.disconnect();
      cumChart.remove();
      ddChart.remove();
    };
  }, [cumData, ddData, palette]);

  if (!cumData.length) return null;

  const pnlColor = finalPnl >= 0 ? '#26a69a' : DD_LINE;

  return (
    <Paper elevation={0} sx={{ bgcolor: BG, border: `1px solid ${GRID}`, borderRadius: 1, mb: 2, overflow: 'hidden' }}>
      {/* Header row */}
      <Box sx={{
        px: 2, py: 1, bgcolor: BG_HDR, borderBottom: `1px solid ${GRID}`,
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: palette.line, flexShrink: 0 }} />
          <Typography sx={{ color: '#d1d4dc', fontWeight: 700, fontSize: '0.78rem' }}>{name}</Typography>
        </Box>

        <Chip label={`Net P&L  ${finalPnl >= 0 ? '+' : ''}${fmt(finalPnl)}`} size="small" sx={{
          fontSize: '0.65rem', fontWeight: 700, height: 20,
          bgcolor: `${pnlColor}1a`, color: pnlColor, border: `1px solid ${pnlColor}50`,
        }} />

        <Chip label={`Max DD  ${fmt(maxDd)}  (${fmt(maxDdPct)}%)`} size="small" sx={{
          fontSize: '0.65rem', fontWeight: 700, height: 20,
          bgcolor: 'rgba(239,83,80,0.12)', color: DD_LINE, border: `1px solid rgba(239,83,80,0.35)`,
        }} />

        <Chip label={`${cumData.length} closed trades`} size="small" sx={{
          fontSize: '0.65rem', height: 20,
          bgcolor: 'rgba(145,153,166,0.1)', color: TICK_CLR,
        }} />
      </Box>

      {/* Cumulative P&L panel */}
      <Box sx={{ px: 1.5, pt: 0.75, pb: 0.25, bgcolor: BG }}>
        <Typography sx={{ color: TICK_CLR, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Cumulative P&amp;L
        </Typography>
      </Box>
      <Box ref={cumRef} sx={{ width: '100%' }} />

      {/* Drawdown panel */}
      <Box sx={{ px: 1.5, pt: 0.75, pb: 0.25, bgcolor: BG, borderTop: `1px solid ${GRID}` }}>
        <Typography sx={{ color: TICK_CLR, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Drawdown
        </Typography>
      </Box>
      <Box ref={ddRef} sx={{ width: '100%' }} />
    </Paper>
  );
}

export default function StrategyCharts({ allTrades, strategies, selectedIds }) {
  const stratMap = useMemo(() => {
    const m = {};
    strategies.forEach(s => { m[s.id] = s.name; });
    return m;
  }, [strategies]);

  const tradesByStrategy = useMemo(() => {
    const groups = {};
    allTrades.forEach(t => {
      const sid = t.entry?.strategy_id;
      if (!sid) return;
      if (!groups[sid]) groups[sid] = [];
      groups[sid].push(t);
    });
    return groups;
  }, [allTrades]);

  const visible = selectedIds.filter(id => tradesByStrategy[id]?.some(t => t.exit));
  if (!visible.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ShowChart sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={700}>Performance Charts</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          scroll to zoom · drag to pan
        </Typography>
      </Box>

      {visible.map((sid, idx) => (
        <SingleStrategyChart
          key={sid}
          name={stratMap[sid] || sid}
          palette={PALETTE[idx % PALETTE.length]}
          trades={tradesByStrategy[sid]}
        />
      ))}
    </Box>
  );
}
